import React, { useState, useContext } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import InputGroup from 'react-bootstrap/InputGroup';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import { AppContext } from '../window';
import { AuthorizedUser } from '../models/User';
import Loader from '../libs/Loader';
import { NotificationContext } from '../context/NotificationContext';
import { useTranslation } from '../i18n';

interface LoginModalProps {
  show: boolean;
  onHide: () => void;
  onLoginSuccess?: () => void;
  onShowSignup?: () => void;
  onShowPasswordReset?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ show, onHide, onLoginSuccess, onShowSignup, onShowPasswordReset }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const appContext = useContext(AppContext);
  const { addNotification } = useContext(NotificationContext);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      // Make fetch request to login API using form-urlencoded
      const formData = new URLSearchParams();
      formData.append('LoginForm[email]', email);
      formData.append('LoginForm[password]', password);

      const response = await fetch('/user/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Clear form
        // setEmail('');
        setPassword('');
        appContext.setUser(data.user as AuthorizedUser)
        if (onLoginSuccess) {
          onLoginSuccess();
        }
        onHide();
      } else {
        setErrorMessage(data.error || t('auth.login_failed'));
      }
    } catch (error) {
      setErrorMessage(t('auth.error_occurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('auth.login')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {errorMessage && (
          <Alert variant="danger">{errorMessage}</Alert>
        )}

        {isSubmitting && (
          <Loader />
        )}

        <Form onSubmit={handleSubmit} className={isSubmitting ? 'd-none' : ''}>
          <Form.Group className="mb-3" controlId="formEmail">
            <Form.Label>{t('auth.email_address')}</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <EmailIcon fontSize="small" />
              </InputGroup.Text>
              <Form.Control 
                type="email" 
                placeholder={t('auth.enter_email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formPassword">
            <Form.Label>{t('auth.password')}</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <LockIcon fontSize="small" />
              </InputGroup.Text>
              <Form.Control 
                type="password" 
                placeholder={t('auth.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </InputGroup>
          </Form.Group>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button 
              variant="link" 
              className="p-0 text-decoration-none" 
              onClick={() => {
                onHide();
                if (onShowPasswordReset) {
                  onShowPasswordReset();
                }
              }}
              style={{
                fontSize: '0.9rem',
                fontWeight: 500,
                background: 'linear-gradient(120deg, #007bff, #0056b3)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              {t('auth.forgot_password')}
            </Button>
          </div>
          
          <Button 
            variant="primary" 
            type="submit" 
            className="w-100"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('auth.logging_in') : t('auth.login')}
          </Button>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="w-100 text-center">
          {t('auth.no_account')}{' '}
          <Button 
            variant="link" 
            className="p-0" 
            onClick={() => {
              onHide();
              if (onShowSignup) {
                onShowSignup();
              }
            }}
          >
            {t('auth.register_now')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default LoginModal;
